import { DARK_GREY } from 'src/lib/player/constants';
import IconMapping from 'src/lib/player/iconmapping.json';
import { FlagContent, type LatLng, type StarterType } from 'src/lib/player/store';
import tinycolor from 'tinycolor2';

export enum DrawInfoType {
  MARKER = 'MARKER',
  POI = 'POI',
  POI_LONG = 'POI_LONG',
  POI_IMAGE = 'POI_IMAGE',
}

export type DrawInfo = {
  id: string;
  title: string;
  label: string;
  bgColor: string;
  textColor: string;
  borderColor: string;
  latlng: LatLng;
  clickable: boolean;
  type: DrawInfoType;
  tickless?: boolean;
  icon: string | null;
  image?: HTMLImageElement;
};

export function createPoiDrawInfo(
  id: string,
  label: string,
  title: string,
  latlng: LatLng,
  color: string,
  longTitle = false,
  icon: string | null = null,
): DrawInfo {
  const _color = tinycolor(color);
  const type = getPoiType(icon, longTitle);
  let iconUrl = icon;

  if (iconUrl != null && Object.keys(IconMapping).includes(iconUrl)) {
    iconUrl = `/img/map-icons/${iconUrl}_borderless.svg`;
  }
  const bgColor = color;
  const borderColor = DARK_GREY;

  return {
    id,
    title,
    label,
    bgColor,
    textColor: !_color.isValid() || _color.isLight() ? 'black' : 'white',
    borderColor,
    latlng,
    clickable: false,
    type,
    icon: iconUrl,
  };
}

const getPoiType = (icon: string | null, longTitle: boolean): DrawInfoType => {
  if (icon != null) {
    return DrawInfoType.POI_IMAGE;
  }

  return longTitle ? DrawInfoType.POI_LONG : DrawInfoType.POI;
};

const MAX_STARTER_LABEL_LENGTH = 48;

export function createStarterDrawInfo(
  starter: StarterType,
  latlng: LatLng,
  flagContent: FlagContent,
): DrawInfo {
  const fullLabel = [starter.startNumber, starter.name]
    .filter((a) => a != null && a !== '')
    .join('. ');
  let label = starter.startNumber ?? '';
  if (flagContent === FlagContent.NAME) {
    label = starter.name ?? '';
  } else if (flagContent === FlagContent.STARTNUMBER_AND_NAME) {
    label = fullLabel;
  } else if (flagContent === FlagContent.TWO_LETTER_NAMES) {
    label = [starter.startNumber, ...(starter.name?.split(' ').map((a) => a.substring(0, 2)) || '')]
      .filter((a) => a != null && a !== '')
      .join('');
  } else if (flagContent === FlagContent.ONE_LETTER_NAMES) {
    label = [starter.startNumber, ...(starter.name?.split(' ').map((a) => a.substring(0, 1)) || '')]
      .filter((a) => a != null && a !== '')
      .join('');
  }
  // Cap label length so a very long name can't exceed the spritesheet bin width.
  // See https://github.com/racemap/gears/issues/3399 for background on the limit.
  if (label.length > MAX_STARTER_LABEL_LENGTH) {
    label = `${label.slice(0, MAX_STARTER_LABEL_LENGTH - 3)}...`;
  }
  return {
    id: starter.id,
    title: fullLabel,
    label,
    bgColor: starter.markerColor.rgba,
    textColor: !starter.markerColor.isLight ? 'white' : 'black',
    borderColor: DARK_GREY,
    latlng,
    clickable: true,
    type: DrawInfoType.MARKER,
    icon: null,
  };
}
